import React, { useState, useEffect, useCallback } from 'react';
import { Comment } from '../types';
import { clientAPI } from '../services/api';

interface CommentsProps {
  clientId: string | number;
  currentUser: string;
}

const Comments: React.FC<CommentsProps> = ({ clientId, currentUser }) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'current' | 'history'>('current');

  const loadComments = useCallback(async () => {
    try {
      const response = await clientAPI.getComments(clientId);
      const commentsData = response.data?.data || response.data || [];
      setComments(Array.isArray(commentsData) ? commentsData : []);
    } catch (error) {
      console.error('Error loading comments:', error);
      setComments([]);
    }
  }, [clientId]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    setLoading(true);
    try {
      await clientAPI.addComment(clientId, {
        content: newComment.trim(),
        created_by: currentUser
      });
      setNewComment('');
      await loadComments();
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Error adding comment');
    } finally {
      setLoading(false);
    }
  };

  const recentComments = comments.slice(0, 3);
  const allComments = comments;

  return (
    <div style={{ marginTop: '20px' }}>
      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#374151' }}>
        Comments & Notes
      </label>
      
      <div style={{ border: '1px solid #d1d5db', borderRadius: '6px', backgroundColor: '#f9fafb' }}>
        {/* Tab Navigation */}
        <div style={{ 
          display: 'flex', 
          borderBottom: '1px solid #d1d5db',
          backgroundColor: '#f3f4f6'
        }}>
          <button
            onClick={() => setActiveTab('current')}
            style={{
              padding: '12px 16px',
              border: 'none',
              backgroundColor: activeTab === 'current' ? 'white' : 'transparent',
              borderBottom: activeTab === 'current' ? '2px solid #2563eb' : '2px solid transparent',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              color: activeTab === 'current' ? '#2563eb' : '#6b7280'
            }}
          >
            Add Comment
          </button>
          <button
            onClick={() => setActiveTab('history')}
            style={{
              padding: '12px 16px',
              border: 'none',
              backgroundColor: activeTab === 'history' ? 'white' : 'transparent',
              borderBottom: activeTab === 'history' ? '2px solid #2563eb' : '2px solid transparent',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              color: activeTab === 'history' ? '#2563eb' : '#6b7280'
            }}
          >
            History ({comments.length})
          </button>
        </div>

        <div style={{ padding: '16px' }}>
          {activeTab === 'current' ? (
            <>
              {/* Add new comment */}
              <div style={{ marginBottom: '16px' }}>
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment or note..."
                  style={{
                    width: '100%',
                    minHeight: '80px',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
                <button
                  onClick={handleAddComment}
                  disabled={loading || !newComment.trim()}
                  style={{
                    marginTop: '8px',
                    padding: '6px 12px',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    opacity: loading || !newComment.trim() ? 0.5 : 1
                  }}
                >
                  {loading ? 'Adding...' : 'Add Comment'}
                </button>
              </div>

              {/* Recent comments */}
              <div>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                  Recent Comments
                </h4>
                {recentComments.length === 0 ? (
                  <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>No comments yet</p>
                ) : (
                  recentComments.map((comment) => (
                    <div
                      key={comment.id}
                      style={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '4px',
                        padding: '12px',
                        marginBottom: '8px'
                      }}
                    >
                      <div style={{ fontSize: '14px', color: '#374151', marginBottom: '4px' }}>
                        {comment.content}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        By {comment.created_by} • {new Date(comment.created_at || '').toLocaleString()}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            /* History tab */
            <div>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                All Comments & Notes History
              </h4>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {allComments.length === 0 ? (
                  <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>No comments in history</p>
                ) : (
                  allComments.map((comment) => (
                    <div
                      key={comment.id}
                      style={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '4px',
                        padding: '12px',
                        marginBottom: '8px'
                      }}
                    >
                      <div style={{ fontSize: '14px', color: '#374151', marginBottom: '4px' }}>
                        {comment.content}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        By {comment.created_by} • {new Date(comment.created_at || '').toLocaleString()}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Comments;
